import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import BookingHeader from "./components/BookingHeader";
import { DesktopIcon } from "./components/Shared";
import {
  getBookingActivitySummaryService,
  getBookingActivityLogsService,
} from "../../../../services/adminBookings";
import { toast } from "react-toastify";

const CalendarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const SearchIcon = () => (
  <svg
    className="h-4 w-4 text-slate-400"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

const FILTER_ACTIVITY_OPTIONS = [
  { value: "all", label: "All Actions" },
  { value: "BOOK_CREATED", label: "Booked" },
  { value: "BOOK_CANCELLED", label: "Cancelled" },
  { value: "Blocked", label: "Blocked" },
  { value: "Unblocked", label: "Unblocked" },
];

const FILTER_BOOKING_TYPE_OPTIONS = [
  { value: "all", label: "All Booking Types" },
  { value: "One-time", label: "One - Time" },
  { value: "Permanent", label: "Permanent" },
  { value: "Recurring", label: "Recurring" },
  { value: "Date-Range", label: "Date Range" },
];

const FILTER_WORKSTATION_OPTIONS = [
  { value: "all", label: "All Workstations" },
  { value: "Laptop", label: "Laptop" },
  { value: "Desktop", label: "Desktop" },
  { value: "Monitor", label: "Monitor" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "6px",
    borderColor: state.isFocused ? "#e2e8f0" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(15, 23, 42, 0.06)" : "none",
    paddingLeft: "4px",
    paddingRight: "4px",
    fontSize: "0.875rem",
    fontWeight: 600,
    "&:hover": { borderColor: "#cbd5e1" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#0f172a",
    fontWeight: 600,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontWeight: 600,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#64748b",
    padding: "8px",
    "&:hover": { color: "#0f172a" },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "#64748b",
    padding: "8px",
    "&:hover": { color: "#0f172a" },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 30,
    borderRadius: "16px",
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.875rem",
    fontWeight: 600,
    backgroundColor: state.isSelected
      ? "#0f172a"
      : state.isFocused
        ? "#f8fafc"
        : "white",
    color: state.isSelected ? "white" : "#0f172a",
    "&:active": { backgroundColor: state.isSelected ? "#0f172a" : "#e2e8f0" },
  }),
};

const getDisplayUserName = (userObj, fallback = "-") => {
  if (!userObj) return fallback;
  if (typeof userObj === "string") return userObj;
  return (
    `${userObj.first_name || ""} ${userObj.last_name || ""}`.trim() ||
    userObj.email ||
    userObj.name ||
    fallback
  );
};

const ActivityLog = () => {
  const [loadingCards, setLoadingCards] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);

  const [filterMonthYear, setFilterMonthYear] = useState(new Date());
  const [filterActionDate, setFilterActionDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActivity, setFilterActivity] = useState("all");
  const [filterBookingType, setFilterBookingType] = useState("all");
  const [filterWorkstation, setFilterWorkstation] = useState("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    total: 0,
    hasNext: false,
  });

  const fetchSummary = async (date) => {
    setLoadingCards(true);
    try {
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const res = await getBookingActivitySummaryService({ month, year });
      const data = res.data?.data || res.data || {};
      setSummary(data);
    } catch (err) {
      toast.error(err.message || "Unable to fetch summary");
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchLogs = async (
    date,
    currentPage,
    search,
    activity,
    bookingType,
    workstation,
    actionDate,
  ) => {
    setLoadingTable(true);
    try {
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      const formattedActionDate = actionDate
        ? new Date(
            actionDate.getTime() - actionDate.getTimezoneOffset() * 60000,
          )
            .toISOString()
            .split("T")[0]
        : undefined;

      const params = {
        month,
        year,
        page: currentPage,
        limit: 10,
        search: search.trim() || undefined,
        action: activity !== "all" ? activity : undefined,
        booking_type: bookingType !== "all" ? bookingType : undefined,
        seat_capability: workstation !== "all" ? workstation : undefined,
        start_date: formattedActionDate,
      };
      const res = await getBookingActivityLogsService(params);
      const data = res.data?.data || res.data || [];
      const meta = res.data?.pagination || res.data?.meta || {};

      setLogs(Array.isArray(data) ? data : data.logs || []);
      setPagination({
        total: meta.total_records || meta.total || data.total || 0,
        totalPages:
          meta.total_pages || meta.totalPages || data.total_pages || 1,
        hasNext:
          typeof meta.has_next_page === "boolean"
            ? meta.has_next_page
            : typeof meta.hasNext === "boolean"
              ? meta.hasNext
              : currentPage < (meta.total_pages || meta.totalPages || 1),
      });
    } catch (err) {
      toast.error(err.message || "Unable to fetch logs");
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    fetchSummary(filterMonthYear);
  }, [filterMonthYear]);

  useEffect(() => {
    fetchLogs(
      filterMonthYear,
      page,
      searchTerm,
      filterActivity,
      filterBookingType,
      filterWorkstation,
      filterActionDate,
    );
  }, [
    filterMonthYear,
    page,
    searchTerm,
    filterActivity,
    filterBookingType,
    filterWorkstation,
    filterActionDate,
  ]);

  const handleMonthChange = (date) => {
    setFilterMonthYear(date);
    setPage(1);
  };

  const selectedActivityOption =
    FILTER_ACTIVITY_OPTIONS.find((o) => o.value === filterActivity) || null;
  const selectedBookingTypeOption =
    FILTER_BOOKING_TYPE_OPTIONS.find((o) => o.value === filterBookingType) ||
    null;
  const selectedWorkstationOption =
    FILTER_WORKSTATION_OPTIONS.find((o) => o.value === filterWorkstation) ||
    null;

  return (
    <div className="container mx-auto pb-10">
      <div className="mb-4">
        {/* Top Navigation */}
        <div className="mb-3 flex items-center gap-3">
          <Link
            to="/bookings"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
          >
            <ArrowLeftIcon />
          </Link>

          <nav className="flex items-center gap-2 text-sm font-medium">
            <Link
              to="/bookings"
              className="text-slate-500 transition hover:text-slate-900"
            >
              Bookings
            </Link>

            <span className="text-slate-300">/</span>

            <span className="font-semibold text-slate-900">Activity Log</span>
          </nav>
        </div>

        <BookingHeader title="Activity Log" subtitle="Seat Management" />
      </div>

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {loadingCards ? (
          <>
            <div className="col-span-1 rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm animate-pulse">
              <div className="h-4 w-24 rounded bg-slate-200 mb-4"></div>
              <div className="h-8 w-16 rounded bg-slate-200"></div>
            </div>
            <div className="col-span-1 sm:col-span-2 lg:col-span-2 rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm animate-pulse">
              <div className="h-4 w-24 rounded bg-slate-200 mb-4"></div>
              <div className="h-8 w-16 rounded bg-slate-200"></div>
            </div>
            <div className="col-span-1 rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm animate-pulse">
              <div className="h-4 w-24 rounded bg-slate-200 mb-4"></div>
              <div className="h-8 w-16 rounded bg-slate-200"></div>
            </div>
          </>
        ) : (
          <>
            <div className="col-span-1 flex flex-col justify-center rounded-[24px] border border-slate-200 bg-white p-6 shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Total Actions
              </p>
              <h3 className="mt-2 text-3xl font-black tracking-tight text-slate-900">
                {summary?.total_actions || 0}
              </h3>
            </div>
            <div className="col-span-1 sm:col-span-2 lg:col-span-2 rounded-[24px] border border-slate-200 bg-white p-6 shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Total Bookings
              </p>
              <h3 className="mt-1.5 text-3xl font-black tracking-tight text-slate-900">
                {summary?.booking_count || 0}
              </h3>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-blue-500" />
                  <span className="text-md font-bold text-slate-900 leading-none min-w-[22px] text-center">
                    {summary?.one_time_bookings || 0}
                  </span>
                  <span className="text-sm font-medium text-slate-500 whitespace-nowrap">
                    One-Time
                  </span>
                </div>

                <div className="h-5 w-px bg-slate-200" />

                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span className="text-md font-bold text-slate-900 leading-none min-w-[22px] text-center">
                    {summary?.date_range_bookings || 0}
                  </span>
                  <span className="text-sm font-medium text-slate-500 whitespace-nowrap">
                    Date Range
                  </span>
                </div>

                <div className="h-5 w-px bg-slate-200" />

                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-violet-500" />
                  <span className="text-md font-bold text-slate-900 leading-none min-w-[22px] text-center">
                    {summary?.permanent_assignments || 0}
                  </span>
                  <span className="text-sm font-medium text-slate-500 whitespace-nowrap">
                    Permanent
                  </span>
                </div>

                <div className="h-5 w-px bg-slate-200" />

                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  <span className="text-md font-bold text-slate-900 leading-none min-w-[22px] text-center">
                    {summary?.recurring_series_created || 0}
                  </span>
                  <span className="text-sm font-medium text-slate-500 whitespace-nowrap">
                    Recurring
                  </span>
                </div>
              </div>
            </div>
            <div className="col-span-1 flex flex-col justify-center rounded-[24px] border border-slate-200 bg-white p-6 shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Cancellations
              </p>
              <h3 className="mt-2 text-3xl font-black tracking-tight text-rose-500">
                {summary?.cancel_count || 0}
              </h3>
            </div>
          </>
        )}
      </div>

      <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_48px_rgba(15,23,42,0.08)]">
        <div className="border-b border-slate-100 bg-[linear-gradient(180deg,#ffffff_0%,#fbfbfb_100%)] px-5 py-5 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="relative admin-custom-date-picker">
                <DatePicker
                  selected={filterMonthYear}
                  onChange={handleMonthChange}
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                  portalId="root"
                  customInput={
                    <button
                      type="button"
                      className="flex items-center gap-3 rounded-md border border-slate-900 bg-slate-900 px-5 py-3 text-white shadow-[0_14px_32px_rgba(15,23,42,0.18)] transition-all hover:bg-slate-800"
                    >
                      <CalendarIcon />
                      <span className="text-sm font-black tracking-wide">
                        {filterMonthYear.toLocaleString("default", {
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                    </button>
                  }
                />
              </div>
              {/* <div className="relative admin-custom-date-picker ml-2">
                <DatePicker
                  selected={filterActionDate}
                  onChange={(date) => {
                    setFilterActionDate(date);
                    setPage(1);
                  }}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Action Date"
                  portalId="root"
                  customInput={
                    <button
                      type="button"
                      className="flex items-center gap-3 rounded-md border border-slate-200 bg-white px-5 py-3 text-slate-700 shadow-sm transition-all hover:bg-slate-50"
                    >
                      <CalendarIcon />
                      <span className="text-sm font-bold tracking-wide">
                        {filterActionDate
                          ? filterActionDate.toLocaleDateString("default", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "Action Date"}
                      </span>
                      {filterActionDate && (
                        <div
                          className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-slate-200 hover:bg-slate-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterActionDate(null);
                            setPage(1);
                          }}
                        >
                          <svg
                            className="h-3 w-3 text-slate-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      )}
                    </button>
                  }
                />
              </div> */}
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-2 xl:gap-3">
              {/* Search */}
              <div className="w-full lg:flex-1 lg:min-w-0 lg:max-w-[500px] xl:max-w-[560px] 2xl:max-w-[620px]">
                <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-3 shadow-sm transition-all">
                  <SearchIcon />

                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search"
                    className="w-full bg-transparent text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400"
                  />

                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        setPage(1);
                      }}
                      className="rounded-md bg-slate-100 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 transition hover:bg-slate-200"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:flex lg:items-center lg:gap-1 xl:gap-2">
                <div className="w-full lg:w-[125px] xl:w-[145px] 2xl:w-[160px]">
                  <Select
                    value={selectedWorkstationOption}
                    onChange={(o) => {
                      setFilterWorkstation(o?.value || "all");
                      setPage(1);
                    }}
                    options={FILTER_WORKSTATION_OPTIONS}
                    isClearable={selectedWorkstationOption?.value !== "all"}
                    isSearchable={false}
                    placeholder="Workstation"
                    styles={selectStyles}
                    classNamePrefix="dropdownSelect"
                  />
                </div>

                <div className="w-full lg:w-[125px] xl:w-[145px] 2xl:w-[160px]">
                  <Select
                    value={selectedBookingTypeOption}
                    onChange={(o) => {
                      setFilterBookingType(o?.value || "all");
                      setPage(1);
                    }}
                    options={FILTER_BOOKING_TYPE_OPTIONS}
                    isClearable={selectedBookingTypeOption?.value !== "all"}
                    isSearchable={false}
                    placeholder="Booking Type"
                    styles={selectStyles}
                    classNamePrefix="dropdownSelect"
                  />
                </div>

                <div className="w-full lg:w-[125px] xl:w-[145px] 2xl:w-[160px]">
                  <Select
                    value={selectedActivityOption}
                    onChange={(o) => {
                      setFilterActivity(o?.value || "all");
                      setPage(1);
                    }}
                    options={FILTER_ACTIVITY_OPTIONS}
                    isClearable={selectedActivityOption?.value !== "all"}
                    isSearchable={false}
                    placeholder="Action"
                    styles={selectStyles}
                    classNamePrefix="dropdownSelect"
                  />
                </div>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between gap-2 sm:justify-end lg:ml-auto">
                <button
                  type="button"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="h-10 rounded-md border border-slate-200 bg-white px-3 lg:px-3.5 xl:px-5 text-[10px] lg:text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>

                <span className="whitespace-nowrap text-[10px] lg:text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
                  Page {page}
                  {pagination.totalPages ? ` / ${pagination.totalPages}` : ""}
                </span>

                <button
                  type="button"
                  onClick={() => setPage(page + 1)}
                  disabled={!pagination.hasNext}
                  className="h-10 rounded-md border border-slate-200 bg-white px-3 lg:px-3.5 xl:px-5 text-[10px] lg:text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-5 sm:px-6 min-h-[500px]">
          {loadingTable && logs.length === 0 ? (
            <div className="space-y-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className="h-14 w-full rounded-xl bg-slate-100 animate-pulse"
                ></div>
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">
              No activity logs found for the selected criteria.
            </div>
          ) : (
            <div
              className={`overflow-x-auto rounded-2xl border border-slate-100 transition-opacity duration-200 ${loadingTable ? "opacity-50 pointer-events-none" : "opacity-100"}`}
            >
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                      Activity
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                      Seat
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                      Section
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                      Booked For
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                      Booking Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                      Workstation
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                      Performed By
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                      Action Date & Time
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log, index) => {
                    const bookingType = (
                      log.booking_type ||
                      log.bookingType ||
                      "one-time"
                    ).toLowerCase();
                    const seatCapability = (
                      log.seat_capability ||
                      log.workstation ||
                      log.seatType ||
                      "laptop"
                    ).toLowerCase();
                    const activityType =
                      log.action || log.activity || log.activity_type || "-";

                    const formatDateTime = (isoString) => {
                      if (!isoString) return "-";
                      const d = new Date(isoString);
                      return isNaN(d.getTime())
                        ? isoString
                        : `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}, ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
                    };

                    const formatDate = (isoString) => {
                      if (!isoString) return "-";
                      const d = new Date(isoString);
                      return isNaN(d.getTime())
                        ? isoString
                        : d.toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          });
                    };

                    return (
                      <tr
                        key={log._id || log.id || index}
                        className="hover:bg-slate-50"
                      >
                        <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">
                          {getDisplayUserName(log.user || log.userName, "-")}
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {activityType === "BOOK_CREATED"
                            ? "Booking Created"
                            : activityType === "BOOK_CANCELLED"
                              ? "Booking Cancelled"
                              : activityType === "BOOK_UPDATED"
                                ? "Booking Updated"
                                : activityType.replace(/_/g, " ")}
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {log.seat_number || log.seat || log.seatLabel || "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {log.section_name || log.section || "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {formatDate(log.booking_date || log.bookingDate)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {bookingType === "permanent" ? (
                            <span className="inline-flex rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-medium text-violet-700 capitalize">
                              Permanent
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium capitalize text-slate-600">
                              {bookingType}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize tracking-wide ${
                              seatCapability === "monitor"
                                ? "bg-blue-50 text-blue-700 border border-blue-100"
                                : seatCapability === "desktop"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                  : "bg-amber-50 text-amber-700 border border-amber-100"
                            }`}
                          >
                            {seatCapability === "monitor" ? (
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <rect
                                  x="2"
                                  y="3"
                                  width="20"
                                  height="14"
                                  rx="2"
                                />
                                <line x1="8" y1="21" x2="16" y2="21" />
                                <line x1="12" y1="17" x2="12" y2="21" />
                              </svg>
                            ) : seatCapability === "desktop" ? (
                              <DesktopIcon className="w-3.5 h-3.5" />
                            ) : (
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <rect
                                  x="2"
                                  y="4"
                                  width="20"
                                  height="14"
                                  rx="2"
                                />
                                <path d="M2 18h20" />
                              </svg>
                            )}
                            {seatCapability}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {getDisplayUserName(
                            log.performed_by || log.performedBy,
                            "-",
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                          {formatDateTime(
                            log.activity_date_local ||
                              log.createdAt ||
                              log.created_at,
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
